import type { Actor, DashboardResponse, JiraIssue, PullRequestDetail } from '@w2w-dx/contracts';
import { type InjectionKey, inject } from 'vue';

/**
 * The single way the SPA talks to the API.
 *
 * Everything goes to the same origin: CloudFront serves the app and routes
 * `/api/*` to API Gateway, so the session cookie is a first-party cookie and
 * there is no CORS preflight to pay for. Nothing here ever sees a GitHub
 * token - those stay encrypted server-side and are spent under the signed-in
 * user's own identity, which is what makes GitHub enforce that person's real
 * permissions on an approval or a merge.
 */

/** Header the API checks on every state-changing request. */
export const CSRF_HEADER = 'X-DX-CSRF';

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

/**
 * Statuses where retrying the same request later might actually work.
 * A 500 is not on the list, and neither is 409 `not_mergeable`: that one
 * means a human has to rebase, and retrying it would hide the fact.
 */
const TRANSIENT_STATUSES = new Set([429, 502, 503, 504]);

const pullPath = (repo: string, number: number): string =>
  `/api/pulls/${encodeURIComponent(repo)}/${number}`;

export const API_ROUTES = {
  me: '/api/me',
  login: '/api/auth/github/login',
  logout: '/api/auth/logout',
  dashboard: '/api/dashboard',
  pullRequest: pullPath,
  /**
   * One endpoint for all three review actions, distinguished by an `event`
   * in the body, mirroring GitHub's own API. The client previously assumed
   * three separate paths and would have 404d on every approval.
   */
  review: (repo: string, number: number) => `${pullPath(repo, number)}/review`,
  merge: (repo: string, number: number) => `${pullPath(repo, number)}/merge`,
  headBranch: (repo: string, number: number) => `${pullPath(repo, number)}/head-branch`,
  rerunChecks: (repo: string, number: number) => `${pullPath(repo, number)}/rerun-failed-checks`,
  jiraIssue: (key: string) => `/api/jira/${encodeURIComponent(key)}`,
} as const;

/** The three values the API accepts for a review's `event`. */
export type ReviewEvent = 'APPROVE' | 'REQUEST_CHANGES' | 'COMMENT';

/**
 * `GET /api/me`. More than an `Actor`, so it has its own shape: the CSRF
 * token lives here because the session cookie is `__Host-` and http-only and
 * therefore unreadable from script.
 */
export interface MeResponse {
  viewer: Actor;
  csrfToken: string;
  /** When org membership was last confirmed against GitHub. */
  orgVerifiedAt: string;
}

/** GitHub's three merge strategies. This org squash-merges by convention. */
export type MergeMethod = 'squash' | 'merge' | 'rebase';

export interface MergeOptions {
  method?: MergeMethod;
  /** Delete the head branch once the merge succeeds. */
  deleteBranch?: boolean;
  commitTitle?: string;
  commitBody?: string;
}

export interface MergeResult {
  merged: boolean;
  /** Merge commit SHA, when GitHub returned one. */
  sha: string | null;
  /**
   * False when the merge landed but the branch deletion did not. The API
   * treats that as a success with a caveat rather than a failed merge, and so
   * should the UI.
   */
  branchDeleted: boolean;
}

/** Error body shape the API uses throughout. */
interface ApiErrorBody {
  error?: string;
  message?: string;
  [extra: string]: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  /** Machine-readable code, taken from the body's `error` field. */
  readonly code: string;
  /** Anything else the API attached, such as `mergeStateStatus` on a 409. */
  readonly details: Readonly<Record<string, unknown>>;

  constructor(
    status: number,
    code: string,
    message: string,
    details: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  get isTransient(): boolean {
    // Status 0 is "no response at all" - a dropped connection, which is the
    // most retryable case there is.
    return this.status === 0 || TRANSIENT_STATUSES.has(this.status);
  }
}

export interface RequestOptions {
  signal?: AbortSignal;
  /** Sent as JSON. Omit for requests without a body. */
  body?: unknown;
  /**
   * Suppress the global 401 handler for this call. Used by the auth store,
   * which wants to decide for itself what an unauthenticated response means
   * rather than being redirected out from under itself at start-up.
   */
  quiet?: boolean;
}

export interface JiraIssueOptions extends RequestOptions {
  /**
   * Ask for the comment list. Off by default, and deliberately so: the hover
   * card must not pay for a second Jira call per hover. Without it the API
   * returns `comments: null`, meaning "not fetched", which is a different
   * thing from an empty array.
   */
  comments?: boolean;
}

interface SendOptions extends RequestOptions {
  headers?: Record<string, string>;
}

async function readErrorBody(response: Response): Promise<ApiErrorBody> {
  try {
    const body: unknown = await response.json();
    return typeof body === 'object' && body !== null ? (body as ApiErrorBody) : {};
  } catch {
    // A non-JSON error body (a CloudFront or gateway page) tells us nothing
    // useful; the status code is the only signal worth acting on.
    return {};
  }
}

function toApiError(response: Response, body: ApiErrorBody, path: string): ApiError {
  const { error, message, ...details } = body;
  return new ApiError(
    response.status,
    error ?? 'request_failed',
    message ?? `Request to ${path} failed (${response.status})`,
    details,
  );
}

async function parseBody<T>(response: Response): Promise<T> {
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T;
  }
  return (await response.json()) as T;
}

export class ApiClient {
  private readonly fetchImpl: typeof globalThis.fetch;
  private csrfToken: string | null = null;
  private onUnauthorized: (() => void) | null = null;

  /** Last dashboard ETag and payload, so polling can be answered with a 304. */
  private dashboardEtag: string | null = null;
  private dashboardCache: DashboardResponse | null = null;

  constructor(fetchImpl: typeof globalThis.fetch = globalThis.fetch.bind(globalThis)) {
    this.fetchImpl = fetchImpl;
  }

  /**
   * Called once per unauthenticated response so the shell can send the user
   * to the sign-in screen. Registered by the router rather than imported
   * here, because the client must not depend on routing.
   */
  setUnauthorizedHandler(handler: (() => void) | null): void {
    this.onUnauthorized = handler;
  }

  setCsrfToken(token: string | null): void {
    this.csrfToken = token;
  }

  /**
   * The token is held only in memory. It cannot be read from a cookie: the
   * session cookie is `__Host-` and http-only, and the token is handed out in
   * the body of `GET /api/me` instead.
   */
  currentCsrfToken(): string | null {
    return this.csrfToken;
  }

  /** Forgets the cached dashboard, so the next poll cannot be answered from it. */
  resetDashboardCache(): void {
    this.dashboardEtag = null;
    this.dashboardCache = null;
  }

  async request<T>(method: string, path: string, options: RequestOptions = {}): Promise<T> {
    const response = await this.exchange(method, path, options);
    return parseBody<T>(response);
  }

  /**
   * Sends the request and resolves only on a response worth parsing. Handles
   * the two failures that are the client's business rather than the caller's:
   * an expired session, and a CSRF token the API no longer recognises.
   */
  private async exchange(
    method: string,
    path: string,
    options: SendOptions,
    isRetry = false,
  ): Promise<Response> {
    const response = await this.send(method, path, options);

    if (response.status === 401) {
      this.csrfToken = null;
      if (!options.quiet) this.onUnauthorized?.();
      const body = await readErrorBody(response);
      throw new ApiError(
        401,
        body.error ?? 'unauthorized',
        body.message ?? 'Your session has expired. Sign in again.',
      );
    }

    if (response.ok || response.status === 304) return response;

    const body = await readErrorBody(response);

    // A rotated or dropped token is recoverable exactly once: fetch a fresh
    // one and replay. Looping on it would turn a server-side problem into a
    // request storm.
    if (response.status === 403 && body.error === 'csrf_invalid' && !isRetry) {
      await this.getMe({ quiet: true });
      return this.exchange(method, path, options, true);
    }

    throw toApiError(response, body, path);
  }

  private async send(method: string, path: string, options: SendOptions): Promise<Response> {
    const headers = new Headers({ Accept: 'application/json', ...options.headers });

    if (options.body !== undefined) headers.set('Content-Type', 'application/json');

    if (!SAFE_METHODS.has(method)) {
      const token = this.csrfToken;
      if (token) headers.set(CSRF_HEADER, token);
    }

    const init: RequestInit = {
      method,
      headers,
      // Same origin behind one CloudFront distribution: the session cookie
      // rides along and no other origin can ever receive it.
      credentials: 'same-origin',
    };

    if (options.body !== undefined) init.body = JSON.stringify(options.body);
    if (options.signal) init.signal = options.signal;

    try {
      return await this.fetchImpl(path, init);
    } catch (cause) {
      // An aborted poll is a normal part of tearing down a view, not a fault.
      if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
      throw new ApiError(0, 'network_error', 'Could not reach the dashboard API.');
    }
  }

  get<T>(path: string, options: RequestOptions = {}): Promise<T> {
    return this.request<T>('GET', path, options);
  }

  post<T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    return this.request<T>('POST', path, body === undefined ? options : { ...options, body });
  }

  patch<T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    return this.request<T>('PATCH', path, body === undefined ? options : { ...options, body });
  }

  delete<T>(path: string, options: RequestOptions = {}): Promise<T> {
    return this.request<T>('DELETE', path, options);
  }

  // --- Session -------------------------------------------------------------

  /**
   * Identity plus the CSRF token. Called at start-up and again if the API
   * ever rejects a token; nothing can be written until it has succeeded once.
   */
  async getMe(options: RequestOptions = {}): Promise<MeResponse> {
    const me = await this.get<MeResponse>(API_ROUTES.me, options);
    this.csrfToken = me.csrfToken;
    return me;
  }

  async logout(options: RequestOptions = {}): Promise<void> {
    await this.post<void>(API_ROUTES.logout, undefined, options);
    this.csrfToken = null;
    this.resetDashboardCache();
  }

  /**
   * Where the "Sign in with GitHub" button points. It must be followed as a
   * full-page navigation rather than fetched: the endpoint 302s to github.com
   * and the OAuth state cookie does not survive an XHR.
   */
  signInUrl(returnTo?: string): string {
    if (!returnTo) return API_ROUTES.login;
    return `${API_ROUTES.login}?redirect=${encodeURIComponent(returnTo)}`;
  }

  // --- Reads ---------------------------------------------------------------

  /**
   * Conditional on the last ETag. The dashboard is polled, and every poll
   * that is not answered with a 304 costs GitHub rate limit that the whole
   * org shares.
   */
  async getDashboard(options: RequestOptions = {}): Promise<DashboardResponse> {
    const send: SendOptions = { ...options };
    if (this.dashboardEtag) send.headers = { 'If-None-Match': this.dashboardEtag };

    const response = await this.exchange('GET', API_ROUTES.dashboard, send);

    if (response.status === 304) {
      if (this.dashboardCache) return this.dashboardCache;
      // Nothing cached to satisfy the 304 with. Drop the ETag so the next
      // call asks unconditionally rather than looping on an empty hit.
      this.resetDashboardCache();
      throw new ApiError(304, 'stale_cache', 'The dashboard cache was lost; retrying.');
    }

    this.dashboardEtag = response.headers.get('etag');
    this.dashboardCache = await parseBody<DashboardResponse>(response);
    return this.dashboardCache;
  }

  getPullRequest(
    repo: string,
    number: number,
    options: RequestOptions = {},
  ): Promise<PullRequestDetail> {
    return this.get<PullRequestDetail>(API_ROUTES.pullRequest(repo, number), options);
  }

  getJiraIssue(key: string, options: JiraIssueOptions = {}): Promise<JiraIssue> {
    const { comments, ...request } = options;
    const path = comments ? `${API_ROUTES.jiraIssue(key)}?comments=1` : API_ROUTES.jiraIssue(key);
    return this.get<JiraIssue>(path, request);
  }

  // --- Writes --------------------------------------------------------------
  // Each of these is performed by the API with the signed-in user's own
  // GitHub token, so GitHub applies that person's permissions rather than the
  // app installation's.

  private review(repo: string, number: number, event: ReviewEvent, body?: string): Promise<void> {
    return this.post<void>(API_ROUTES.review(repo, number), {
      event,
      ...(body ? { body } : {}),
    });
  }

  /** An approval may carry no comment; the other two must. */
  approve(repo: string, number: number, body?: string): Promise<void> {
    return this.review(repo, number, 'APPROVE', body);
  }

  requestChanges(repo: string, number: number, body: string): Promise<void> {
    return this.review(repo, number, 'REQUEST_CHANGES', body);
  }

  comment(repo: string, number: number, body: string): Promise<void> {
    return this.review(repo, number, 'COMMENT', body);
  }

  /**
   * `deleteBranch` is always sent explicitly. The API defaults it to false so
   * the destructive option is opt-in at the HTTP layer, while the product
   * default here is to tidy up; relying on either default would make the
   * behaviour depend on which side you read.
   */
  merge(repo: string, number: number, options: MergeOptions = {}): Promise<MergeResult> {
    return this.post<MergeResult>(API_ROUTES.merge(repo, number), {
      method: options.method ?? 'squash',
      deleteBranch: options.deleteBranch ?? true,
      ...(options.commitTitle ? { commitTitle: options.commitTitle } : {}),
      ...(options.commitBody ? { commitBody: options.commitBody } : {}),
    });
  }

  /** Standalone cleanup. Returns 409 `pull_not_merged` while the PR is open. */
  deleteBranch(repo: string, number: number): Promise<void> {
    return this.delete<void>(API_ROUTES.headBranch(repo, number));
  }

  rerunFailedChecks(repo: string, number: number): Promise<void> {
    return this.post<void>(API_ROUTES.rerunChecks(repo, number));
  }
}

/** Shared instance, provided at the app root and injectable below. */
export const api = new ApiClient();

export const API_CLIENT_KEY: InjectionKey<ApiClient> = Symbol('w2w-dx.api-client');

/**
 * Resolves the provided client, falling back to the shared instance so a
 * component mounted on its own in a test still works without extra wiring.
 */
export function useApi(): ApiClient {
  return inject(API_CLIENT_KEY, api);
}
